import { Reducer } from 'interfaces'
import { UserImage, UserImagesAppState } from 'interfaces/userImage'
import {
  createEmptyCollection,
  getItemFromCollection,
  setItemInCollection,
  removeByKeyFromCollection,
  concatCollections,
  normalize,
} from 'helpers/normalizeHelpers'
import { USER_IMAGE_UPLOAD, USER_IMAGE_LOCAL_UPDATE } from 'actions/userImageActions'
import { isRequestAction, isFailAction, isSuccessAction } from 'helpers/actionsHelpers'
import { GET_POSTS, GET_FOLLOWED_USERS, GET_COMMENTS, GET_FEED_MEMBER_INFO, LOAD_COMMENT_THREAD } from 'actions/feedActions'
import { FEED_COMPOSE_IMAGE_REMOVE, COMPOSE_SUBMIT, FEED_COMPOSE_FETCH_LINK } from 'actions/feedComposeActions'
import { normalizeImage } from 'helpers/feedHelpers'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_SOCIAL_PROFILE, FORM_MEMBER_PROFILE } from 'constants/forms'

export const userImagesReducer: Reducer<UserImagesAppState> = (prevState = createEmptyCollection(), action) => {
  switch (action.type) {
    case USER_IMAGE_UPLOAD: {
      const { id, file, type } = action.payload.params
      if (isRequestAction(action)) {
        const nextImageState = {
          id,
          file,
          type,
          isLoading: true,
          errorCode: null,
        }
        const nextState = setItemInCollection<UserImage, 'id'>(prevState, nextImageState, 'id')
        return nextState
      }

      const imageState = getItemFromCollection<UserImage, 'id'>(prevState, id)
      if (!imageState) {
        return prevState
      }

      if (isFailAction(action)) {
        const { errorCode } = action.payload
        const nextState = setItemInCollection<UserImage, 'id'>(prevState, {
          ...imageState,
          isLoading: false,
          errorCode,
        }, 'id')
        return nextState
      }

      {
        const { image } = action.payload.response
        const nextState = setItemInCollection<UserImage, 'id'>(prevState, {
          ...imageState,
          image,
          isLoading: false,
        }, 'id')
        return nextState
      }
    }

    case USER_IMAGE_LOCAL_UPDATE: {
      const { id, localImage } = action.payload

      const imageState = getItemFromCollection<UserImage, 'id'>(prevState, id)
      if (!imageState) {
        return prevState
      }

      const nextState = setItemInCollection<UserImage, 'id'>(prevState, {
        ...imageState,
        localImage,
      }, 'id')
      return nextState
    }

    case FEED_COMPOSE_IMAGE_REMOVE: {
      const { id } = action.payload.params
      return removeByKeyFromCollection<UserImage, 'id'>(prevState, id)
    }

    case GET_POSTS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { images } = action.payload.response
      return concatCollections(prevState, images)
    }

    case GET_FOLLOWED_USERS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { avatars: images } = action.payload.response
      return concatCollections(prevState, images)
    }

    case LOAD_COMMENT_THREAD: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { images } = action.payload.response
      return concatCollections(prevState, images)
    }

    case GET_COMMENTS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { images } = action.payload.response
      return concatCollections(prevState, images)
    }

    case COMPOSE_SUBMIT: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { images } = action.payload.response
      return concatCollections(prevState, normalize(images, 'id'))
    }

    case FEED_COMPOSE_FETCH_LINK: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { type, image } = action.payload.response
      if (type !== 'image' || !image) {
        return prevState
      }

      const nextState = setItemInCollection(prevState, normalizeImage(image), 'id')
      return nextState

    }

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (action.payload.formName !== FORM_SOCIAL_PROFILE && action.payload.formName !== FORM_MEMBER_PROFILE) {
        return prevState
      }
      let nextState = prevState

      const avatarId = action.payload.formValues.avatar
      if (avatarId) {
        nextState = removeByKeyFromCollection<UserImage, 'id'>(prevState, avatarId)
      }

      const { response } = action.payload
      if (response.avatar) {
        nextState = setItemInCollection(prevState, normalizeImage(response.avatar), 'id')
      }

      return nextState
    }

    case GET_FEED_MEMBER_INFO: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { avatar } = action.payload.response
      if (!avatar) {
        return prevState
      }

      const nextState = setItemInCollection(prevState, avatar, 'id')
      return nextState
    }
  }
  return prevState
}
